import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { Navbar, PageHeader, Button, Input } from '../components';
import LoadingSpinner from '../components/LoadingSpinner';
import friendService from '../services/friendService';

function getInstagramHandle(socialLink) {
  if (!socialLink) return null;
  const match = socialLink.match(/instagram\.com\/([^/?]+)/);
  if (match) return match[1];
  return socialLink.replace('@', '');
}

export default function FriendRequestsPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();

  const [tab, setTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [respondingId, setRespondingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  // People search state
  const [peopleSearch, setPeopleSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Suggested friends state
  const [suggestedFriends, setSuggestedFriends] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);

  // Track sent request userIds (from search/suggestions)
  const [sentRequestIds, setSentRequestIds] = useState(new Set());

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      const [friendsResult, pendingResult, sentResult] = await Promise.all([
        friendService.getFriends(),
        friendService.getPendingRequests(),
        friendService.getSentRequests(),
      ]);
      if (friendsResult.success) {
        setFriends(friendsResult.data || []);
      } else {
        setError(friendsResult.error || 'Failed to load friends');
      }
      if (pendingResult.success) {
        setReceived(pendingResult.data || []);
      }
      if (sentResult.success) {
        setSent(sentResult.data || []);
      }
      setIsLoading(false);
    }
    fetchData();
  }, []);

  const handleRespond = async (requestId, accept) => {
    setRespondingId(requestId);
    const result = await friendService.respondToRequest(requestId, accept);
    if (result.success) {
      setReceived(prev => prev.filter(r => r.id !== requestId));
      if (accept) {
        // Refresh friends list to show new friend
        const friendsResult = await friendService.getFriends();
        if (friendsResult.success) {
          setFriends(friendsResult.data || []);
        }
      }
    }
    setRespondingId(null);
  };

  const handleCancel = async (requestId) => {
    setRespondingId(requestId);
    const result = await friendService.cancelRequest(requestId);
    if (result.success) {
      setSent(prev => prev.filter(r => r.id !== requestId));
    }
    setRespondingId(null);
  };

  const handleRemoveFriend = async (friendUserId, displayName) => {
    if (!window.confirm(`Remove ${displayName} from your friends?`)) return;
    setRemovingId(friendUserId);
    const result = await friendService.removeFriend(friendUserId);
    if (result.success) {
      setFriends(prev => prev.filter(f => f.userId !== friendUserId));
    }
    setRemovingId(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Fetch suggested friends
  useEffect(() => {
    const fetchSuggestions = async () => {
      setIsLoadingSuggestions(true);
      const result = await friendService.getSuggestedFriends(6);
      if (result.success) {
        setSuggestedFriends(result.data || []);
      }
      setIsLoadingSuggestions(false);
    };
    fetchSuggestions();
  }, []);

  // Search for people (debounced)
  useEffect(() => {
    if (!peopleSearch.trim() || peopleSearch.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      const result = await friendService.searchPeople(peopleSearch);
      if (result.success) {
        setSearchResults(result.data || []);
      }
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [peopleSearch]);

  const handleSendFriendRequest = async (userId) => {
    const result = await friendService.sendFriendRequest(userId);
    if (result.success) {
      setSentRequestIds((prev) => new Set([...prev, userId]));
      // Update search results
      setSearchResults((prev) =>
        prev.map((p) => (p.userId === userId ? { ...p, requestSent: true } : p))
      );
      // Update suggested friends
      setSuggestedFriends((prev) =>
        prev.map((p) => (p.userId === userId ? { ...p, requestSent: true } : p))
      );
      // Refresh sent requests list
      const sentResult = await friendService.getSentRequests();
      if (sentResult.success) {
        setSent(sentResult.data || []);
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate(profile?.initialMode === 'host' ? '/host' : '/guest')}
      />

      {/* Tabs */}
      <div className="max-w-3xl mx-auto">
        <PageHeader
          title="FRIENDS"
          subtitle={`${friends.length} friends · ${received.length} received · ${sent.length} sent`}
        />
        <div className="flex border-b-2 border-black">
          <button
            onClick={() => setTab('friends')}
            className={`flex-1 px-6 py-3 font-mono text-sm font-bold uppercase tracking-wider transition-colors ${
              tab === 'friends'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            FRIENDS{friends.length > 0 ? ` (${friends.length})` : ''}
          </button>
          <button
            onClick={() => setTab('received')}
            className={`flex-1 px-6 py-3 font-mono text-sm font-bold uppercase tracking-wider border-l-2 border-black transition-colors ${
              tab === 'received'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            RECEIVED{received.length > 0 ? ` (${received.length})` : ''}
          </button>
          <button
            onClick={() => setTab('sent')}
            className={`flex-1 px-6 py-3 font-mono text-sm font-bold uppercase tracking-wider border-l-2 border-black transition-colors ${
              tab === 'sent'
                ? 'bg-black text-white'
                : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            SENT{sent.length > 0 ? ` (${sent.length})` : ''}
          </button>
        </div>

        {isLoading ? (
          <div className="px-6 py-16 text-center">
            <LoadingSpinner label="LOADING..." />
          </div>
        ) : error ? (
          <div className="px-6 py-16 text-center border-b-2 border-black">
            <div className="font-mono text-lg font-bold text-gray-400 mb-2">ERROR</div>
            <div className="font-mono text-sm text-gray-400">{error}</div>
          </div>
        ) : tab === 'friends' ? (
          /* Friends List */
          friends.length === 0 ? (
            <div className="px-6 py-16 text-center border-b-2 border-black">
              <div className="font-mono text-lg font-bold text-gray-400 mb-2">NO FRIENDS YET</div>
              <div className="font-mono text-sm text-gray-400">
                Send friend requests to connect with people.
              </div>
            </div>
          ) : (
            <div className="divide-y-2 divide-black border-b-2 border-black">
              {friends.map((friend) => (
                <div
                  key={friend.userId}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/users/${friend.userId}`)}
                      className="flex items-center gap-4 flex-1 min-w-0 text-left hover:opacity-70 transition-opacity"
                    >
                      <div className="w-10 h-10 border-2 border-black overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                        {friend.avatarUrl ? (
                          <img
                            src={friend.avatarUrl}
                            alt={friend.displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="font-mono text-base font-bold text-black">
                            {friend.displayName?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-sm font-bold text-black truncate">
                          {friend.displayName}
                        </p>
                        {friend.handle && (
                          <p className="font-mono text-xs text-gray-500 truncate">
                            @{friend.handle}
                          </p>
                        )}
                        {friend.vibeCheck && (
                          <p className="font-mono text-xs text-gray-400 truncate">
                            {friend.vibeCheck}
                          </p>
                        )}
                        {friend.friendsSince && (
                          <p className="font-mono text-[10px] text-gray-400 mt-0.5">
                            Friends since {new Date(friend.friendsSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={() => handleRemoveFriend(friend.userId, friend.displayName)}
                      disabled={removingId === friend.userId}
                      className="px-4 py-2 border-2 border-black text-black font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-100 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {removingId === friend.userId ? '...' : 'REMOVE'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Received / Sent Requests */
          (() => {
            const requests = tab === 'received' ? received : sent;
            return requests.length === 0 ? (
              <div className="px-6 py-16 text-center border-b-2 border-black">
                <div className="font-mono text-lg font-bold text-gray-400 mb-2">
                  {tab === 'received' ? 'NO PENDING REQUESTS' : 'NO SENT REQUESTS'}
                </div>
                <div className="font-mono text-sm text-gray-400">
                  {tab === 'received'
                    ? "When someone sends you a friend request, it'll show up here."
                    : "Friend requests you've sent will appear here."}
                </div>
              </div>
            ) : (
              <div className="divide-y-2 divide-black border-b-2 border-black">
                {requests.map((request) => {
                  const person = tab === 'received' ? request.senderProfile : request.receiverProfile;
                  return (
                    <div
                      key={request.id}
                      className="px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => navigate(`/users/${person?.userId}`)}
                          className="flex items-center gap-4 flex-1 min-w-0 text-left hover:opacity-70 transition-opacity"
                        >
                          <div className="w-10 h-10 border-2 border-black overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                            {person?.avatarUrl ? (
                              <img
                                src={person.avatarUrl}
                                alt={person.displayName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-mono text-base font-bold text-black">
                                {person?.displayName?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-mono text-sm font-bold text-black truncate">
                              {person?.displayName}
                            </p>
                            {person?.handle && (
                              <p className="font-mono text-xs text-gray-500 truncate">
                                @{person.handle}
                              </p>
                            )}
                            {person?.socialLink && (
                              <p className="font-mono text-xs text-gray-400 truncate">
                                IG: @{getInstagramHandle(person.socialLink)}
                              </p>
                            )}
                            {request.message && (
                              <p className="font-mono text-xs text-gray-600 mt-1 truncate">
                                "{request.message}"
                              </p>
                            )}
                          </div>
                        </button>
                        {tab === 'received' ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleRespond(request.id, true)}
                              disabled={respondingId === request.id}
                              className="px-4 py-2 bg-black text-white font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50"
                            >
                              ACCEPT
                            </button>
                            <button
                              onClick={() => handleRespond(request.id, false)}
                              disabled={respondingId === request.id}
                              className="px-4 py-2 border-2 border-black text-black font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                              DENY
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleCancel(request.id)}
                            disabled={respondingId === request.id}
                            className="px-4 py-2 border-2 border-black text-black font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-100 transition-colors disabled:opacity-50 flex-shrink-0"
                          >
                            CANCEL
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}

        {/* People You May Know Section */}
        <div className="border-2 border-black bg-white mt-8">
          <div className="px-6 py-4 border-b-2 border-black">
            <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
              PEOPLE YOU MAY KNOW
            </h2>
          </div>

          {isLoadingSuggestions ? (
            <div className="p-8">
              <LoadingSpinner label="Finding people..." />
            </div>
          ) : suggestedFriends.length === 0 ? (
            <div className="p-8 text-center">
              <p className="font-mono text-sm text-gray-500 uppercase tracking-wider">
                No suggestions available
              </p>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                {suggestedFriends.map((person) => (
                  <div
                    key={person.userId}
                    className="border-2 border-black p-4 flex flex-col items-center bg-white"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/users/${person.userId}`)}
                      className="flex flex-col items-center w-full hover:opacity-70 transition-opacity"
                    >
                      <div className="w-16 h-16 mb-3 border-2 border-black overflow-hidden bg-gray-100">
                        {person.avatarUrl ? (
                          <img
                            src={person.avatarUrl}
                            alt={person.displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="font-mono text-xl text-gray-400">?</span>
                          </div>
                        )}
                      </div>
                      <p className="font-mono text-xs font-bold uppercase tracking-wider text-black text-center truncate w-full mb-3">
                        {person.displayName}
                      </p>
                    </button>
                    <Button
                      size="sm"
                      variant={person.requestSent ? 'secondary' : 'primary'}
                      fullWidth
                      onClick={() => handleSendFriendRequest(person.userId)}
                      disabled={person.requestSent}
                    >
                      {person.requestSent ? 'SENT' : '+ ADD'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* People Search Section */}
        <div className="border-2 border-black bg-white p-6 mt-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by Name or PR Handle..."
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
              />
            </div>
            <Button
              variant="primary"
              disabled={!peopleSearch.trim() || isSearching}
            >
              {isSearching ? 'SEARCHING...' : 'SEARCH'}
            </Button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 divide-y divide-gray-200">
              {searchResults.map((person) => (
                <div key={person.userId} className="py-3 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/users/${person.userId}`)}
                    className="flex items-center gap-4 flex-1 min-w-0 text-left hover:opacity-70 transition-opacity"
                  >
                    <div className="w-10 h-10 border-2 border-black overflow-hidden flex-shrink-0 bg-gray-100">
                      {person.avatarUrl ? (
                        <img src={person.avatarUrl} alt={person.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="font-mono text-sm text-gray-400">?</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm font-bold uppercase tracking-wider text-black truncate">
                        {person.displayName}
                      </p>
                      {person.handle && (
                        <p className="font-mono text-xs text-gray-500">@{person.handle}</p>
                      )}
                    </div>
                  </button>
                  <Button
                    size="sm"
                    variant={person.requestSent ? 'secondary' : 'primary'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendFriendRequest(person.userId);
                    }}
                    disabled={person.requestSent}
                  >
                    {person.requestSent ? 'SENT' : '+ ADD'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
